import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'urql';
import { useExportState } from '../components/common/ExportStateManagerContext';
import { ResolvedOrderEntry } from '../domain/resolve-order-entry';
import { GetProjectDeepDocument } from '../gql/graphql';
import { ResolvedOrder, useResolvedOrders } from '../hooks/order';
import { exportTableToExcel } from '../utils/excel-utils';
import { exportRoofsToXml, XmlMaterial, XmlRoof } from '../utils/xml-utils';

interface ExportProjectDataParams {
  projectId: string;
}

export const ExportProjectData = () => {
  const { projectId } = useParams<
    keyof ExportProjectDataParams
  >() as ExportProjectDataParams;

  const {
    setIsSuccessful,
    setIsExporting,
    columnsToExport,
    exportType,
    ordersToExport,
    selectedRoofNames,
  } = useExportState();

  const [{ data: projectData, error: projectDataError }] = useQuery({
    query: GetProjectDeepDocument,
    variables: {
      id: projectId,
    },
  });

  const project = projectData?.project;
  const projectOrders =
    project?.orders.filter((order) => !order.isAggregatedOrder) || [];

  const { resolvedOrders, error: errorResolvingOrders } = useResolvedOrders(
    projectId,
    projectOrders
  );

  useEffect(() => {
    if (resolvedOrders && projectData?.project?.name) {
      if (exportType === 'prokalk') {
        const allRoofs = buildXmlRoofsFromRoofData(resolvedOrders);
        const roofs = (() => {
          if (!selectedRoofNames || selectedRoofNames.length === 0)
            return allRoofs;
          const normalize = (s: unknown) =>
            String(s ?? '')
              .trim()
              .toLowerCase();
          const selected = new Set(selectedRoofNames.map(normalize));
          const filtered = allRoofs.filter((r) =>
            selected.has(normalize(r.Name))
          );
          return filtered;
        })();
        exportRoofsToXml(
          roofs,
          `Sparkel Export - ${
            projectData?.project?.name
          } - ${new Date().toUTCString()}.xml`
        );
      } else {
        const tableToExport = prepareExport(resolvedOrders, columnsToExport);
        exportTableToExcel({
          ...tableToExport,
          filename: `Sparkel Export - ${
            projectData?.project?.name
          } - ${new Date().toUTCString()}.xlsx`,
        });
      }

      setIsSuccessful(true);
      setIsExporting(false);
    }
  }, [
    projectData?.project?.name,
    resolvedOrders,
    setIsSuccessful,
    setIsExporting,
    columnsToExport,
    exportType,
    ordersToExport,
    selectedRoofNames,
  ]);

  useEffect(() => {
    if (errorResolvingOrders) {
      console.error(errorResolvingOrders);
    }
    if (projectDataError) {
      console.error(projectDataError);
    }
  }, [errorResolvingOrders, projectDataError]);

  return <></>;
};

export function buildXmlRoofsFromRoofData(orders: ResolvedOrder[]): XmlRoof[] {
  const roofDataOrder = orders.find((o) => o.name === 'Roof Data');
  if (!roofDataOrder) {
    return [];
  }

  const roofRows: ResolvedOrderEntry[] = [];
  for (const entry of roofDataOrder.orderEntries) {
    if (entry.subRows && entry.subRows.length > 0) {
      roofRows.push(...entry.subRows);
    }
  }

  const roofs: XmlRoof[] = [];
  for (const roofRow of roofRows) {
    const roofName = roofRow.name;

    // Collect materials across all orders for this roof name
    const materials: XmlMaterial[] = [];
    for (const order of orders) {
      for (const topEntry of order.orderEntries) {
        collectMaterialsFromEntryFiltered(topEntry, order, materials, roofName);
      }
    }

    const getField = (fieldName: string) =>
      getCustomFieldByName(roofRow, roofDataOrder, fieldName);

    type RoofField = Exclude<
      keyof XmlRoof,
      'Name' | 'Materials' | 'NetRoofSqm'
    >;

    const ROOF_DYNAMIC_FIELDS: readonly RoofField[] = [
      'Surface',
      'ParapetLengthLm',
      'ParapetSurface',
      'ParapetHeight',
      'Roofing',
      'VerticalAreaLength',
      'CombustibleInsulationSqm',
      'CombustibleInsulationAvgThickness',
      'SlopeTypeEPS',
      'EpsKileLm',
      'MwKileLm',
      'NonCombustibleInsulationSqm',
      'NonCombustibleInsulationAvgThickness',
      'SlopeTypeMw',
      'NonCombustibleInsulationFireSecSqm',
      'NonCombustibleInsulationFireSecAvgThickness',
      'VerticalAreaSqm',
      'CornersNum',
      'TapeDiffusionBarrier',
    ] as const;

    const dynamicFields = ROOF_DYNAMIC_FIELDS.reduce((acc, key) => {
      const value = getField(String(key));
      const isEmptyString = typeof value === 'string' && value.trim() === '';
      if (value !== undefined && value !== null && !isEmptyString) {
        (acc as Record<string, number | string | undefined>)[key] = value as
          | number
          | string;
      }
      return acc;
    }, {} as Partial<Pick<XmlRoof, RoofField>>);

    const roof: XmlRoof = {
      Name: roofName,
      NetRoofSqm:
        typeof roofRow.resolvedQuantity?.value === 'number'
          ? roofRow.resolvedQuantity.value
          : undefined,
      Materials: materials,
      ...dynamicFields,
    };

    roofs.push(roof);
  }

  return roofs;
}

function collectMaterialsFromEntryFiltered(
  entry: ResolvedOrderEntry,
  order: ResolvedOrder,
  materials: XmlMaterial[],
  roofName: string
) {
  if (entry.subRows && entry.subRows.length > 0) {
    for (const sub of entry.subRows) {
      collectMaterialsFromEntryFiltered(sub, order, materials, roofName);
    }
    return;
  }

  if ((entry?.name ?? '') === roofName) {
    addMaterialFromLeaf(entry, order, materials);
  }
}

// These products are automatically added in ProKalk
const SKU_IGNORE_LIST = ['39001422'];

function addMaterialFromLeaf(
  entry: ResolvedOrderEntry,
  order: ResolvedOrder,
  materials: XmlMaterial[]
) {
  const sku = getCustomFieldByName(entry, order, 'SKU');
  const unit = entry.unit;
  const qty =
    typeof entry.resolvedQuantity?.value === 'number'
      ? entry.resolvedQuantity.value
      : 0;
  const grossRaw = getCustomFieldByName(entry, order, 'Gross Quantity');
  const grossQty = typeof grossRaw === 'number' ? grossRaw : Number(grossRaw);

  if (
    sku &&
    String(sku).trim().length > 0 &&
    String(qty) !== 'NaN' &&
    qty > 0 &&
    !SKU_IGNORE_LIST.includes(String(sku))
  ) {
    materials.push({
      ArticleNo: String(sku),
      NetQuantity: formatQuantity(qty, unit),
      GrossQuantity: isNaN(grossQty)
        ? undefined
        : formatQuantity(grossQty, unit),
    });
  }
}

function formatQuantity(value: number, unit: unknown): string {
  const unitStr = String(unit ?? '').toLowerCase();
  const isPcs =
    unitStr === 'pcs' || unitStr === 'piece' || unitStr === 'pieces';
  if (isPcs) {
    return String(Math.round(value));
  }
  return value.toFixed(2);
}

function getCustomFieldByName(
  entry: ResolvedOrderEntry,
  order: ResolvedOrder,
  name: string
) {
  const cf = entry.customFields.find((f) => {
    const columnName = order.orderColumns.find(
      (c) => c.id === f.orderColumnId
    )?.name;
    return columnName === name;
  });
  return cf?.resolvedValue?.value ?? '';
}

export // Recursive function to handle both entries and sub-rows
const prepareExport = (orders: ResolvedOrder[], columns?: string[]) => {
  function processItem(
    entry: ResolvedOrderEntry,
    order: ResolvedOrder,
    depth: number,
    parentNames: string[] = []
  ): void {
    if (entry.subRows && entry.subRows.length > 0) {
      entry.subRows.forEach((subRow) =>
        processItem(subRow, order, depth + 1, [...parentNames, entry.name])
      );
    } else {
      // Initialize row with empty values for each column
      const row = new Array(headers.length).fill('');

      // Set 'Item Name' and 'Table' values
      row[0] = entry.name;
      row[1] = order.name;

      // Set the label values
      Array.from(labelKeys).forEach((key, labelIndex) => {
        const label = order.labels.find((label) => label.key === key);
        row[2 + labelIndex] = label ? label.value : ''; // +2 for the first two standard columns
      });

      // Set the itemGroup values
      parentNames.forEach((name, index) => {
        row[2 + labelKeys.size + index] = name; // Adjust for label columns
      });

      // Set the Quantity value
      const quantityIndex = 2 + labelKeys.size + maxDepth;
      const quantityValue = entry.resolvedQuantity
        ? Number(entry.resolvedQuantity.value)
        : 0;
      row[quantityIndex] = quantityValue;

      // Set the Unit
      const unitIndex = 3 + labelKeys.size + maxDepth;
      const unitValue = entry.unit;
      row[unitIndex] = unitValue;

      // Set the custom field values
      entry.customFields?.forEach((customField) => {
        const columnName = order.orderColumns.find(
          (column) => column.id === customField.orderColumnId
        )?.name;
        if (columnName) {
          const index = headers.indexOf(columnName);
          if (index !== -1) {
            let cellValue = customField.resolvedValue?.value;

            if (typeof cellValue === 'number' && !isNaN(cellValue)) {
              if (columnName === 'SKU') {
                // Format SKU as a string
                cellValue = cellValue.toString();
              } else {
                cellValue = inferAndFormatValue(cellValue.toString());
              }
            } else if (Array.isArray(cellValue)) {
              cellValue = uniqueRoundedArray(cellValue);
            }

            row[index] = cellValue as string;
          }
        }
      });

      // Set 'Created At' and 'Updated At' values
      row[headers.indexOf('Created At')] = new Date(entry.createdAt) ?? '';
      row[headers.indexOf('Updated At')] = new Date(entry.updatedAt) ?? '';

      rows.push(row);
    }
  }

  function uniqueRoundedArray(
    values: (string | number | (string | number)[])[]
  ): (string | number)[] {
    function roundToNearestFiveDecimals(num: number): number {
      return Math.round(num * 100000) / 100000;
    }
    const uniqueValues = new Set<string | number>();

    values.forEach((value) => {
      if (Array.isArray(value)) {
        value.forEach((innerValue) => {
          if (typeof innerValue === 'number') {
            uniqueValues.add(roundToNearestFiveDecimals(innerValue));
          } else {
            uniqueValues.add(innerValue);
          }
        });
      } else {
        if (typeof value === 'number') {
          uniqueValues.add(roundToNearestFiveDecimals(value));
        } else {
          uniqueValues.add(value);
        }
      }
    });

    return Array.from(uniqueValues);
  }

  // Function to get the depth of subRows
  function getDepth(entry: ResolvedOrderEntry, currentDepth: number): number {
    if (!entry.subRows || entry.subRows.length === 0) {
      return currentDepth;
    }
    return Math.max(
      ...entry.subRows.map((subRow) => getDepth(subRow, currentDepth + 1))
    );
  }

  // Find all unique custom field names
  const customFieldNames = new Set<string>();

  orders.forEach((order) => {
    order.orderEntries.forEach((entry) => {
      entry.customFields?.forEach((customField) => {
        const columnName = order.orderColumns.find(
          (column) => column.id === customField.orderColumnId
        )?.name;
        if (columnName) {
          customFieldNames.add(columnName);
        }
      });
    });
  });

  // Find unique label keys and max depth
  const labelKeys = new Set<string>();
  let maxDepth = 0;
  orders.forEach((order) => {
    order.labels.forEach((label) => labelKeys.add(label.key));
    order.orderEntries.forEach((entry) => {
      const depth = getDepth(entry, 0);
      maxDepth = Math.max(maxDepth, depth);
    });
  });

  // Prepare headers
  const headers: string[] = ['Item Name', 'Table'];
  headers.push(...Array.from(labelKeys)); // Add label headers
  for (let i = 1; i <= maxDepth; i++) {
    headers.push(`itemGroup${i}`); // Add itemGroup headers
  }
  headers.push('Quantity'); // Add 'Quantity' header
  headers.push('Unit'); // Add 'Unit' header
  headers.push(...Array.from(customFieldNames)); // Add unique custom field names as headers
  headers.push('Created At', 'Updated At'); // Add 'Created At' and 'Updated At' headers

  // Prepare rows
  const rows: string[][] = [];
  orders.forEach((order) => {
    order.orderEntries.forEach((entry) => {
      processItem(entry, order, 1);
    });
  });

  // If columns are provided, filter the headers and rows to only include the provided columns.
  // The headers should also be sorted in the same order as the provided columns.
  if (columns && columns.length > 0) {
    const columnSet = new Set(columns);
    const filteredHeaders = columns.filter((column) => columnSet.has(column));
    const headerIndexMap = new Map(
      headers.map((header, index) => [header, index])
    );
    const filteredRows = rows.map((row) =>
      filteredHeaders.map((header) => {
        const index = headerIndexMap.get(header);
        return typeof index === 'number' ? row[index] : undefined;
      })
    ) as string[][];
    return { headers: filteredHeaders, rows: filteredRows };
  }

  return { headers, rows };
};

function inferAndFormatValue(value: string) {
  // Check if the value is numeric and convert it to a number if so
  const numberValue = Number(value);
  if (!isNaN(numberValue) && numberValue.toString() === value.toString()) {
    return numberValue;
  }
  // Return the original value if it's not numeric
  return value;
}
